import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { PageLayout } from '@/components/layout/PageLayout';
import { EnrollHeroSection } from '@/pages/Enroll/sections/EnrollHeroSection';
import {
  EnrollToolbarSection,
  SORT_OPTIONS,
  DEFAULT_SORT,
  DEFAULT_STATUS,
} from '@/pages/Enroll/sections/EnrollToolbarSection';
import { EnrollResultsSection } from '@/pages/Enroll/sections/EnrollResultsSection';
import { DEMO_CLASS_SESSIONS } from '@/pages/Enroll/data';
import { computeStatus, makeToggler } from '@/pages/Enroll/helpers';
import {
  toggleEnrollment as toggleEnrollmentAction,
  toggleSaved as toggleSavedAction,
  selectEnrolledIds,
  selectSavedIds,
} from '@/store/slices/enrollmentSlice';

const VALID_LOCATIONS = new Set(DEMO_CLASS_SESSIONS.map((c) => c.location));

function findInstructorBySlug(slug) {
  const session = DEMO_CLASS_SESSIONS.find((s) => s.instructor.slug === slug);
  return session?.instructor ?? null;
}

function findSubjectByCourseSlug(slug) {
  const session = DEMO_CLASS_SESSIONS.find((s) => s.courseSlug === slug);
  return session?.subject ?? null;
}

export default function Enroll() {
  const [searchParams] = useSearchParams();
  const dispatch = useDispatch();
  const enrolledIds = useSelector(selectEnrolledIds);
  const savedIds = useSelector(selectSavedIds);

  const initialSubjectSlug = searchParams.get('subject');
  const initialSubjectName = initialSubjectSlug
    ? findSubjectByCourseSlug(initialSubjectSlug)
    : null;

  const initialInstructorSlug = searchParams.get('instructor');
  const initialInstructor = initialInstructorSlug
    ? findInstructorBySlug(initialInstructorSlug)
    : null;

  const initialLocations = searchParams
    .getAll('locations')
    .filter((loc) => VALID_LOCATIONS.has(loc));

  const [searchQuery, setSearchQuery] = useState('');
  const [selectedLocations, setSelectedLocations] = useState(initialLocations);
  const [selectedInstructors, setSelectedInstructors] = useState(
    initialInstructor
      ? [`${initialInstructor.firstName} ${initialInstructor.lastName}`]
      : []
  );
  const [selectedSubjects, setSelectedSubjects] = useState(
    initialSubjectName ? [initialSubjectName] : []
  );
  const [selectedFormats, setSelectedFormats] = useState([]);
  const [statusFilter, setStatusFilter] = useState(DEFAULT_STATUS);
  const [sortBy, setSortBy] = useState(DEFAULT_SORT);

  const toggleLocation = makeToggler(setSelectedLocations);
  const toggleInstructor = makeToggler(setSelectedInstructors);
  const toggleSubject = makeToggler(setSelectedSubjects);
  const toggleFormat = makeToggler(setSelectedFormats);

  const allLocations = useMemo(
    () => [...new Set(DEMO_CLASS_SESSIONS.map((c) => c.location))].sort(),
    []
  );
  const allInstructors = useMemo(() => {
    const set = new Set();
    DEMO_CLASS_SESSIONS.forEach((c) =>
      set.add(`${c.instructor.firstName} ${c.instructor.lastName}`)
    );
    return [...set].sort();
  }, []);
  const allSubjects = useMemo(
    () => [...new Set(DEMO_CLASS_SESSIONS.map((c) => c.subject))].sort(),
    []
  );
  const allFormats = useMemo(
    () => [...new Set(DEMO_CLASS_SESSIONS.map((c) => c.format))].sort(),
    []
  );

  const visibleClasses = useMemo(() => {
    let result = DEMO_CLASS_SESSIONS;

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter((c) => {
        const fullName = `${c.instructor.firstName} ${c.instructor.lastName}`.toLowerCase();
        return (
          c.subject.toLowerCase().includes(q) ||
          fullName.includes(q) ||
          c.format.toLowerCase().includes(q) ||
          c.location.toLowerCase().includes(q)
        );
      });
    }
    if (selectedLocations.length > 0) {
      result = result.filter((c) => selectedLocations.includes(c.location));
    }
    if (selectedInstructors.length > 0) {
      result = result.filter((c) => {
        const fullName = `${c.instructor.firstName} ${c.instructor.lastName}`;
        return selectedInstructors.includes(fullName);
      });
    }
    if (selectedSubjects.length > 0) {
      result = result.filter((c) => selectedSubjects.includes(c.subject));
    }
    if (selectedFormats.length > 0) {
      result = result.filter((c) => selectedFormats.includes(c.format));
    }
    if (statusFilter !== DEFAULT_STATUS) {
      result = result.filter(
        (c) => computeStatus(c, enrolledIds.includes(c.id)) === statusFilter
      );
    }

    const sortOption = SORT_OPTIONS.find((opt) => opt.id === sortBy) ?? SORT_OPTIONS[0];
    return [...result].sort(sortOption.sortFn);
  }, [
    searchQuery,
    selectedLocations,
    selectedInstructors,
    selectedSubjects,
    selectedFormats,
    statusFilter,
    sortBy,
    enrolledIds,
  ]);

  function toggleEnrollment(classId) {
    const classSession = DEMO_CLASS_SESSIONS.find((c) => c.id === classId);
    if (!classSession) return;
    const isCurrentlyEnrolled = enrolledIds.includes(classId);
    if (
      !isCurrentlyEnrolled &&
      classSession.enrolledStudents.length >= classSession.maxSeats
    ) {
      return;
    }
    dispatch(toggleEnrollmentAction(classId));
  }

  function toggleSaved(classId) {
    dispatch(toggleSavedAction(classId));
  }

  function clearAllFilters() {
    setSearchQuery('');
    setSelectedLocations([]);
    setSelectedInstructors([]);
    setSelectedSubjects([]);
    setSelectedFormats([]);
    setStatusFilter(DEFAULT_STATUS);
  }

  return (
    <PageLayout>
      <EnrollHeroSection />
      <EnrollToolbarSection
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        sortBy={sortBy}
        onSortChange={setSortBy}
        allLocations={allLocations}
        selectedLocations={selectedLocations}
        onToggleLocation={toggleLocation}
        allInstructors={allInstructors}
        selectedInstructors={selectedInstructors}
        onToggleInstructor={toggleInstructor}
        allSubjects={allSubjects}
        selectedSubjects={selectedSubjects}
        onToggleSubject={toggleSubject}
        allFormats={allFormats}
        selectedFormats={selectedFormats}
        onToggleFormat={toggleFormat}
        statusFilter={statusFilter}
        onStatusChange={setStatusFilter}
        onClearAll={clearAllFilters}
      />
      <EnrollResultsSection
        classes={visibleClasses}
        totalCount={DEMO_CLASS_SESSIONS.length}
        searchQuery={searchQuery}
        onClearAll={clearAllFilters}
        enrolledIds={enrolledIds}
        savedIds={savedIds}
        onToggleEnroll={toggleEnrollment}
        onToggleSave={toggleSaved}
      />
    </PageLayout>
  );
}